import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MaterialModule } from '../../material.module';
import { FormConfigService } from '../forms-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { MatTableDataSource } from '@angular/material/table';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { MatDialog } from '@angular/material/dialog';
import { ClinicInfoComponent } from '../clinic-info/clinic-info.component';

interface TreatmentPlan {
  treatmentPlanId: number;
  patientNumber: number;
  dateTP: string;
  heading: string;
  note: string;
  responsParty: number;
  docNum: number;
  status: string;
  secUserNumEntry: number;
  secDateEntry: string;
  secDateTEdit: string;
  userNumPresenter: number;
  type: string;
  dateSigned: string;
  datePracticeSigned: string;
  signatureText: string;
  signaturePracticeText: string;
}

interface Procedure {
  patientNumber: number;
  procedureNumber: number;
  procedureCode: string;
  procedureDescription: string;
  priority: string;
  procedureStatus: string;
  procedureType: string;
  procedureCategory: string;
  procedureDate: string;
  toothNum: string;
  toothRange: string;
  procedureFee: string;
  surfaces: string;
  allowed: number;
  discount: number;
  primaryInsAmount: number;
  secondaryInsAmount: number;
  patientPortion: number;
}

@Component({
  selector: 'app-treatment-plans',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ClinicInfoComponent,
  ],
  templateUrl: './treatment-plans.component.html',
  styleUrls: ['./treatment-plans.component.scss']
})
export class TreatmentPlansComponent implements OnInit {
  activeTreatmentPlan: TreatmentPlan | null = null;
  procedures: Procedure[] = [];
  clientId = '';
  patientId = '';
  dataSource = new MatTableDataSource<Procedure>();
  noPlansMessage: string = '';
  
  // Basic columns shown in compact view
  compactColumns: string[] = [
    'procedureDescription', 
    'procedureDate', 
    'location',
    'priority', 
    'procedureStatus', 
    'procedureFee', 
    'insurance', 
    'patientPortion'
  ];
  
  // Additional columns for detailed view
  detailedColumns: string[] = [
    'procedureDescription', 
    'procedureDate', 
    'location',
    'priority', 
    'procedureStatus', 
    'procedureFee', 
    'insurance', 
    'patientPortion',
    'toothNum',
    'surfaces',
    'allowed'
  ];
  
  displayedColumns: string[] = this.compactColumns;
  isCompactView = true;
  isMobile = false;
  loading = true;

  // Service injections
  private appStateService = inject(AppStateService);
  private formConfigService = inject(FormConfigService);
  private snackBar = inject(MatSnackBar);
  private breakpointObserver = inject(BreakpointObserver);

  constructor(
    private dialog: MatDialog
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
  }

  ngOnInit(): void {
    // Monitor screen size for responsive design
    this.breakpointObserver.observe(['(max-width: 768px)']).subscribe(result => {
      this.isMobile = result.matches;
      // Update view based on screen size
      this.updateDisplayColumns();
    });
    this.fetchTreatmentPlans();
  }

  /**
   * Update display columns based on view mode and screen size
   */
  updateDisplayColumns(): void {
    this.displayedColumns = this.isCompactView ? this.compactColumns : this.detailedColumns;
  }

  /**
   * Fetch treatment plans from the service
   */
  fetchTreatmentPlans(): void {
    this.loading = true;
    this.formConfigService.fetchTreatmentPlans(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        // Check if the response indicates no plans found
        if (data.summary && data.summary.includes('No treatment plans found')) {
          this.activeTreatmentPlan = null;
          this.procedures = [];
          this.dataSource.data = [];
          this.noPlansMessage = data.summary;
        } else if (data.active_treatment_plan) {
          this.activeTreatmentPlan = data.active_treatment_plan;
          this.procedures = data.procedures ? this.processProcedures(data.procedures) : [];
          this.dataSource.data = this.procedures;
        } else {
          // Any other case where we don't have an active plan
          this.activeTreatmentPlan = null;
          this.procedures = [];
          this.dataSource.data = [];
          this.noPlansMessage = 'No active treatment plans available.';
        }
        this.loading = false;
      },
      error: (err: any) => {
        this.showNotification('Unable to load your treatment plan. Please try again later.');
        this.loading = false;
        this.activeTreatmentPlan = null;
        this.procedures = [];
        this.dataSource.data = [];
      }
    });
  }

  /**
   * Process the raw procedure data
   */
  processProcedures(procedures: Procedure[]): Procedure[] {
    if (!procedures || !procedures.length) {
      return [];
    }
    
    // Sort procedures by priority and date
    return procedures.sort((a, b) => {
      // First sort by priority (High, Medium, Low)
      const priorityOrder = { 'High': 0, 'Medium': 1, 'Low': 2 };
      const priorityA = priorityOrder[a.priority as keyof typeof priorityOrder] || 999;
      const priorityB = priorityOrder[b.priority as keyof typeof priorityOrder] || 999;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then sort by date (newest first)
      const dateA = new Date(a.procedureDate || 0).getTime();
      const dateB = new Date(b.procedureDate || 0).getTime();
      return dateB - dateA;
    });
  }

  /**
   * Toggle between compact and detailed view
   */
  toggleView(): void {
    this.isCompactView = !this.isCompactView;
    this.updateDisplayColumns();
  }

  /**
   * Format a date string to be more readable
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'Not scheduled';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Format as "Mon Day, Year"
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  /**
   * Get a user-friendly description of the procedure location
   */
  getLocationDescription(procedure: Procedure): string {
    const parts = [];
    
    if (procedure.toothNum) {
      parts.push(`Tooth #${procedure.toothNum}`);
    }
    
    if (procedure.toothRange) {
      parts.push(`Teeth ${procedure.toothRange}`);
    }
    
    if (procedure.surfaces) {
      // Convert surface abbreviations to words
      const surfaceMap: {[key: string]: string} = {
        'M': 'Mesial',
        'D': 'Distal',
        'O': 'Occlusal',
        'B': 'Buccal',
        'L': 'Lingual',
        'F': 'Facial'
      };
      
      const surfaces = procedure.surfaces.split('').map(s => 
        surfaceMap[s] || s
      ).join(', ');
      
      parts.push(`Surfaces: ${surfaces}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'General';
  }

  /**
   * Get CSS class based on priority
   */
  getPriorityClass(priority: string): string {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-normal';
    }
  }

  /**
   * Get CSS class based on status
   */
  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'in progress':
        return 'status-in-progress';
      case 'scheduled':
        return 'status-scheduled';
      case 'pending':
        return 'status-pending';
      default:
        return 'status-default';
    }
  }

  /**
   * Calculate total cost of all procedures
   */
  calculateTotalCost(): number {
    if (!this.procedures.length) return 0;
    
    return this.procedures.reduce((total, procedure) => {
      return total + parseFloat(procedure.procedureFee || '0');
    }, 0);
  }

  /**
   * Calculate total insurance coverage
   */
  calculateInsuranceEstimate(): number {
    if (!this.procedures.length) return 0;
    
    return this.procedures.reduce((total, procedure) => {
      return total + (procedure.primaryInsAmount || 0) + (procedure.secondaryInsAmount || 0);
    }, 0);
  }

  /**
   * Calculate total patient responsibility
   */
  calculatePatientResponsibility(): number {
    if (!this.procedures.length) return 0;
    
    return this.procedures.reduce((total, procedure) => {
      return total + (procedure.patientPortion || 0);
    }, 0);
  }

  /**
   * Open contact dialog or initiate phone call on mobile
   */
  contactOffice(): void {
    this.dialog.open(ClinicInfoComponent, {
      width: '400px',
    });
  }

  /**
   * Display a notification to the user
   */
  showNotification(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  /**
   * Generate PDF of the treatment plan
   */
  generatePdf(): void {
    this.showNotification('Preparing your treatment plan for download...');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight: number): void => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
    };

    // Helper function to add page numbers at the end
    const addPageNumbers = (): void => {
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
    };

    // Title
    pdf.setFontSize(20);
    pdf.setTextColor(103, 58, 183); // Primary purple color
    pdf.text('Your Treatment Plan', pageWidth / 2, yPosition + 10, { align: 'center' });
    yPosition += 25;

    // Plan Summary Section
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Plan Summary', margin, yPosition);
    yPosition += 10;

    // Summary boxes
    const boxWidth = contentWidth / 3;
    const boxHeight = 20;

    // Total Treatment Cost
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Total Treatment Cost', margin + 5, yPosition);
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`$${this.calculateTotalCost().toFixed(2)}`, margin + 5, yPosition + 8);

    // Insurance Estimate
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Insurance Estimate', margin + boxWidth + 5, yPosition);
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`$${this.calculateInsuranceEstimate().toFixed(2)}`, margin + boxWidth + 5, yPosition + 8);

    // Your Estimated Responsibility
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Your Estimated Responsibility', margin + (boxWidth * 2) + 5, yPosition);
    pdf.setFontSize(14);
    pdf.setTextColor(103, 58, 183); // Purple for patient responsibility
    pdf.text(`$${this.calculatePatientResponsibility().toFixed(2)}`, margin + (boxWidth * 2) + 5, yPosition + 8);

    yPosition += boxHeight + 10;

    // Recommended Procedures Section
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Recommended Procedures', margin, yPosition);
    yPosition += 10;

    // Table headers
    const colWidths = [55, 22, 30, 18, 18, 22, 22]; // Treatment, When, Location, Priority, Status, Fee, Insurance
    const headers = ['Treatment', 'When', 'Location', 'Priority', 'Status', 'Total Fee', 'Insurance'];

    // Draw header background
    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, yPosition - 4, contentWidth, 10, 'F');

    // Draw headers
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);
    let xPos = margin + 2;
    headers.forEach((header, index) => {
      pdf.text(header, xPos, yPosition + 2);
      xPos += colWidths[index];
    });
    yPosition += 12;

    // Draw table rows
    pdf.setFontSize(8);
    this.procedures.forEach((procedure, index) => {
      // Calculate row height based on content
      const treatmentText = procedure.procedureDescription || '';
      const treatmentLines = pdf.splitTextToSize(treatmentText, colWidths[0] - 4);
      const codeText = `Code: ${procedure.procedureCode || 'N/A'}`;
      const rowHeight = Math.max(treatmentLines.length * 4 + 8, 16);

      // Check if we need a new page before drawing this row
      checkPageBreak(rowHeight + 5);

      // Alternate row background
      if (index % 2 === 0) {
        pdf.setFillColor(252, 252, 252);
        pdf.rect(margin, yPosition - 4, contentWidth, rowHeight, 'F');
      }

      // Draw row content
      xPos = margin + 2;
      pdf.setTextColor(0, 0, 0);

      // Treatment (with code)
      pdf.setFontSize(9);
      treatmentLines.forEach((line: string, lineIndex: number) => {
        pdf.text(line, xPos, yPosition + (lineIndex * 4));
      });
      pdf.setFontSize(7);
      pdf.setTextColor(120, 120, 120);
      pdf.text(codeText, xPos, yPosition + (treatmentLines.length * 4) + 2);
      xPos += colWidths[0];

      // When
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      const dateText = this.formatDate(procedure.procedureDate);
      const dateLines = pdf.splitTextToSize(dateText, colWidths[1] - 2);
      dateLines.forEach((line: string, lineIndex: number) => {
        pdf.text(line, xPos, yPosition + (lineIndex * 4));
      });
      xPos += colWidths[1];

      // Location
      const locationText = this.getLocationDescription(procedure);
      const locationLines = pdf.splitTextToSize(locationText, colWidths[2] - 2);
      locationLines.forEach((line: string, lineIndex: number) => {
        pdf.text(line, xPos, yPosition + (lineIndex * 4));
      });
      xPos += colWidths[2];

      // Priority
      pdf.text(procedure.priority || 'N/A', xPos, yPosition);
      xPos += colWidths[3];

      // Status
      pdf.text(procedure.procedureStatus || 'N/A', xPos, yPosition);
      xPos += colWidths[4];

      // Total Fee
      pdf.text(`$${parseFloat(procedure.procedureFee || '0').toFixed(2)}`, xPos, yPosition);
      xPos += colWidths[5];

      // Insurance
      const insuranceAmount = (procedure.primaryInsAmount || 0) + (procedure.secondaryInsAmount || 0);
      pdf.text(`$${insuranceAmount.toFixed(2)}`, xPos, yPosition);

      yPosition += rowHeight;
    });

    // Add page numbers
    addPageNumbers();

    pdf.save('my-treatment-plan.pdf');
  }
}